import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Bell,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useHealthAlerts, useAcknowledgeAlert, type HealthAlert } from "@/hooks/useRemoteMonitoring";

export function AlertsList() {
  const { data: alerts, isLoading } = useHealthAlerts();
  const acknowledgeMutation = useAcknowledgeAlert();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-24" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const criticalAlerts = alerts?.filter(a => a.type === "critical") || [];
  const warningAlerts = alerts?.filter(a => a.type === "warning") || [];
  const unacknowledgedAlerts = alerts?.filter(a => !a.acknowledged) || [];

  const renderAlert = (alert: HealthAlert) => (
    <div
      key={alert.id}
      className={cn(
        "flex items-center justify-between rounded-lg border p-4 transition-all",
        alert.type === "critical" && !alert.acknowledged
          ? "border-destructive/50 bg-destructive/5"
          : alert.type === "warning" && !alert.acknowledged
          ? "border-warning/50 bg-warning/5"
          : "border-border bg-muted/30 opacity-60"
      )}
    >
      <div className="flex items-center gap-4">
        <div className={cn(
          "rounded-full p-2",
          alert.type === "critical" ? "bg-destructive/10" : "bg-warning/10"
        )}>
          {alert.type === "critical" ? (
            <AlertTriangle className="h-5 w-5 text-destructive" />
          ) : (
            <Bell className="h-5 w-5 text-warning" />
          )}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">{alert.patientName}</h4>
            <Badge
              variant={alert.type === "critical" ? "destructive" : "outline"}
              className={alert.type === "warning" ? "bg-warning/10 text-warning border-warning/50" : ""}
            >
              {alert.type === "critical" ? "Critical" : "Warning"}
            </Badge>
            {alert.acknowledged && (
              <Badge variant="outline" className="gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Acknowledged
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {alert.metric}: <span className="font-medium text-foreground">{alert.value}</span>
            <span className="mx-2">•</span>
            Threshold: {alert.threshold}
          </p>
          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {alert.time}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {!alert.acknowledged && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => acknowledgeMutation.mutate(alert.id)}
            disabled={acknowledgeMutation.isPending}
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />
            Acknowledge
          </Button>
        )}
        <Button variant="ghost" size="sm">
          View Patient
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Active Alerts
            </CardTitle>
            <CardDescription>
              Monitor and acknowledge patient health alerts
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              {criticalAlerts.filter(a => !a.acknowledged).length} Critical
            </Badge>
            <Badge variant="outline" className="gap-1 bg-warning/10 text-warning border-warning/50">
              <Bell className="h-3 w-3" />
              {warningAlerts.filter(a => !a.acknowledged).length} Warning
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList>
            <TabsTrigger value="all">
              All ({alerts?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="unacknowledged">
              Unacknowledged ({unacknowledgedAlerts.length})
            </TabsTrigger>
            <TabsTrigger value="critical">
              Critical ({criticalAlerts.length})
            </TabsTrigger>
            <TabsTrigger value="warning">
              Warning ({warningAlerts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-3">
            {alerts?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-success" />
                <p>No alerts to display</p>
              </div>
            ) : (
              alerts?.map(renderAlert)
            )}
          </TabsContent>

          <TabsContent value="unacknowledged" className="space-y-3">
            {unacknowledgedAlerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-success" />
                <p>All alerts have been acknowledged</p>
              </div>
            ) : (
              unacknowledgedAlerts.map(renderAlert)
            )}
          </TabsContent>

          <TabsContent value="critical" className="space-y-3">
            {criticalAlerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-success" />
                <p>No critical alerts</p>
              </div>
            ) : (
              criticalAlerts.map(renderAlert)
            )}
          </TabsContent>

          <TabsContent value="warning" className="space-y-3">
            {warningAlerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-success" />
                <p>No warning alerts</p>
              </div>
            ) : (
              warningAlerts.map(renderAlert)
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
